import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "iuvity-ui-components/dist/button";
import { NewCustomDialog, NewDialogProps } from "../dialog/NewCustomDialog";
import { TransactionPropCreatorSchema } from "../../validations/schemas";
import { REVALIDATE_FORM_PROPS, infoPropertiesFields } from "../../constants/index";
import { AppContext } from "../../context";
import { useProducts } from "../../hooks/useProducts";
import { Header } from "..";
import { BoxFull, TransactionalSubheader } from "iuvity-ui-components";
import { useNavigate } from "react-router-dom";
import { CustomFormComponent } from "./CustomFormComponent";
import { useVersions } from "../../hooks/useVersions";
import { useProperties } from "../../hooks/useProperties";
import { useTransactions } from "../../hooks/useTransactions";
import { createLabelButtonMaker } from "../../helpers/createLabelButtonMaker";
import { useConsoleTranslation } from "../../hooks/useConsoleTranslations";

interface NewPropertyProps {
  type: "product" | "transaction";
  update?: boolean;
}

const propertyDefaultValues = {
  key: "",
  labelkey: "",
  description: "",
  type: "",
  defaultvalue: "",
  isdefaultforall: false,
  usagelist: null,
  releaseversion: "",
};

export const NewProperty: React.FC<NewPropertyProps> = ({ type,  update = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, language, selectedProperty },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });
  const {
    getValues,
    setValue,
    reset,
    formState: { isDirty, isValid },
  } = form;
  
  const {
    propertyAction,
  } = useProperties("DATA", "product");
  const {
    data: transactionsData,
  } = useTransactions("DATA");
  const { data: productsData } = useProducts("ALL");
  const { data: versionsData } = useVersions("DATA");
  const { data: labelsConsoleData, reloadFn } = useConsoleTranslation("DATA");
  const [modalProps, setModalProps] = useState<NewDialogProps>({
    visible: false,
  } as NewDialogProps);

  const typeItem = type === "transaction" ? "Transactions" : "Products";

  const typeOptions = useMemo(() => {
    if (language) {
      return [
        {
          label: t("GlobalConfigurations.textOptionLabel"),
          value: "text",
        },
        {
          label: t("GlobalConfigurations.numericOptionLabel"),
          value: "number",
        },
        {
          label: t("GlobalConfigurations.booleanOptionLabel"),
          value: "boolean",
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  let hash:any = {};
  const formKeysOptions = useMemo(() => {
    if ((typeOptions && versionsData) && (productsData || transactionsData)) {
      return {
        type: typeOptions?.sort((a, b) =>
          a.label.localeCompare(b.label)),
        usagelist: type === 'product' 
          ? productsData
              ?.map((product) => ({
                label: product?.name,
                value: product?.name,
              }))
              ?.sort((a: any, b: any) => a.label.localeCompare(b.label))
          : transactionsData
              ?.map((transaction) => ({
                label: transaction?.name,
                value: transaction?.name,
              }))
              ?.sort((a: any, b: any) => a.label.localeCompare(b.label)),
        releaseversion: versionsData
          ?.map((version: any) => ({
            label: version?.name,
            value: version?.name,
          }))
          ?.sort((a: any, b: any) => b.label.localeCompare(a.label)),
        labelkey: labelsConsoleData?.filter((item: any) => item?.functionality?.includes(type === "product" ? "Products" : "Transactions"))
          ?.map((item: any)=> ({ label: item?.key, value: item?.key}))
          ?.filter((item: any) => {
            const exists = !hash[item.value];
            hash[item.value] = true;
            return exists;
          })
          ?.sort((a: any, b: any) => a.label.localeCompare(b.label)),
      };
    } else {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOptions, productsData, versionsData, transactionsData, labelsConsoleData]);

  const onReturn = () => {
    // Para volver a validar los permisos cuando swe retorne al Tab de Producto o Transacciones
    setAppState((prev) => ({
      ...prev,
      functionalityCode: type === 'product' ? '/products' : '/transactions',
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionStatus = await propertyAction(update ? "UPDATE" : "CREATE", [
      {
        ...formValues,
        usagetype: type,
        [update ? "updatedby" : "createdby"]: user?.name,
      },
    ]);
    if (actionStatus === "SUCCESS") {
      navigate(-1);
      window.scrollTo(0, 0);
    }
    setAppState((prev) => ({
      ...prev,
      functionalityCode: type === 'product' ? '/products' : '/transactions',
    }));
    reset(propertyDefaultValues);
  };
  
  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev: any) => ({
          ...prev,
          visible: true,
          title: t(`Products.${update ? "save" : "create"}ModalTitle`),
          content: t(`Products.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Products.cancelModalTitle"),
          content: t("Products.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (update && selectedProperty) {
      reset({
        id: selectedProperty.id,
        key: selectedProperty?.key,
        labelkey: selectedProperty?.labelkey,
        description: selectedProperty?.description,
        defaultvalue: selectedProperty?.defaultvalue,
        isdefaultforall: selectedProperty?.isdefaultforall,
        type: selectedProperty?.type,
        usagelist: selectedProperty?.usagelist,
        releaseversion: selectedProperty?.releaseversion,
      });
    } else {
      reset(propertyDefaultValues);
    };
    
    // Elimina chips vacíos del multitenant
    setTimeout(()=>{
      // @ts-ignore
      [...document.getElementsByClassName('p-multiselect-token-label')]?.map((span)=>!span.innerHTML && (()=>{span.parentNode.style.display = 'none'})());
    },10);

  }, [reset, selectedProperty, update]);

  const readOnlyObject = {
    key: update ? true : false,
  }

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update 
              ? t(`${typeItem}.edit${typeItem.slice(0, -1)}PropertyTitle`) 
              : t(`${typeItem}.new${typeItem.slice(0, -1)}PropertyTitle`)
            }
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
           id={t("Products.productAdminTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t(`${typeItem}.${type}AdminTitle`),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update ? t(`${typeItem}.edit${typeItem.slice(0, -1)}PropertyTitle`) : t(`${typeItem}.new${typeItem.slice(0, -1)}PropertyTitle`),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <div id="margin-fixed-bgscreen">
        <CustomFormComponent
          functionality={typeItem}
          fields={infoPropertiesFields}
          form={form}
          dropdownOptions={formKeysOptions}
          schemaOfValidation={TransactionPropCreatorSchema}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
          readOnlyObject={readOnlyObject}
          properties={true}
          dropdownFilterObject={{ labelkey: true }}
          customComponent={createLabelButtonMaker({
            isAdminFunctionality: true,
            setModalProps,
            dialogTitle: t('ConsoleTranslations.createLabelTitle'),
            dialogDetail: t('ConsoleTranslations.createLabelDescription'),
            buttonLabel: t('ConsoleTranslations.createLabelTitle'),
            functionality: type === 'product' ? 'Products' : 'Transactions',
            actionsToExecute:(key: string)=>{
              reloadFn();
              setValue("labelkey", key, REVALIDATE_FORM_PROPS)
            },
          })}
        />
      </div>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            label={t(`${typeItem}.cancelBtnLabel`)}
            onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
            className="mr-4"
            variant="p-button-link"
          />
          <Button
            label={
              update
                ? t(`${typeItem}.saveBtnLabel`)
                : t(`${typeItem}.createBtnLabel`)
            }
            variant="p-button"
            onClick={() => handleModalProps("action")}
            disabled={update ? !(isValid && isDirty) : !isValid}
          />
        </div>
      </BoxFull>

      <NewCustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
        aditionalClass="new-property-component-dialog overlay-createlabelsbutton"
        acceptLabel={modalProps.acceptLabel}
        rejectLabel={modalProps.rejectLabel}
        onReject={modalProps.onReject}
      />
    </>
  );
};
